import { useIntl } from '@doxyme/locale';
import {
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  type ModalFooterProps,
  ModalHeader,
} from '@doxyme/ui/components';
import { useTheme } from '@doxyme/ui-foundation/theme';
import { BodyM, LinkM } from '@doxyme/ui-foundation/typography';
import { AppLink } from '../../../global/appLink';

import { useId, type JSX } from 'react';

export enum TroubleshootingStep {
  Init = 'init',
  Refreshed = 'refreshed',
  RefreshSuccess = 'refreshSuccess',
  BeforeRestart = 'beforeRestart',
  Restarted = 'restarted',
  RestartSuccess = 'restartSuccess',
  Fail = 'fail',
}

type StepDetails = { message: string | JSX.Element; actions: ModalFooterProps };

export type CallTroubleshootingProps = {
  isOpen: boolean;
  onClose: () => void;
  refresh: () => Promise<void>;
  restart: () => Promise<void>;
  isLoading: boolean;
  currentStep: TroubleshootingStep | null;
  onSuccess: (step: TroubleshootingStep) => void;
  onFail: () => void;
  onRefreshFailed: () => void;
};

export const CallTroubleshooting = ({
  isOpen,
  onClose,
  refresh,
  restart,
  isLoading,
  currentStep,
  onSuccess,
  onFail,
  onRefreshFailed,
}: CallTroubleshootingProps): JSX.Element | null => {
  const theme = useTheme();
  const { formatMessage } = useIntl();
  const titleId = useId();

  if (!currentStep) {
    return null;
  }

  const getStepDetails = (): StepDetails | undefined => {
    switch (currentStep) {
      case TroubleshootingStep.Init: {
        return {
          message: formatMessage({ id: 'troubleshooting.initMessage' }),
          actions: {
            submitButton: {
              isLoading,
              onClick: refresh,
              children: formatMessage({ id: 'troubleshooting.refreshVideo' }),
            },
          },
        };
      }
      case TroubleshootingStep.Refreshed: {
        return {
          message: formatMessage({
            id: 'troubleshooting.afterRefreshingMessage',
          }),
          actions: {
            layout: 'equal-buttons',
            submitButton: {
              onClick: onRefreshFailed,
              children: formatMessage({ id: 'yes' }),
            },
            secondaryButton: {
              onClick: () => onSuccess(TroubleshootingStep.RefreshSuccess),
              children: formatMessage({ id: 'no' }),
              type: 'secondary',
            },
          },
        };
      }
      case TroubleshootingStep.BeforeRestart: {
        return {
          message: (
            <>
              {formatMessage(
                { id: 'troubleshooting.restartMessage' },
                {
                  break: <br />,
                },
              )}
            </>
          ),
          actions: {
            submitButton: {
              onClick: restart,
              children: formatMessage({ id: 'troubleshooting.restartCall' }),
            },
          },
        };
      }
      case TroubleshootingStep.Restarted: {
        return {
          message: formatMessage({ id: 'troubleshooting.afterRestartCall' }),
          actions: {
            layout: 'equal-buttons',
            submitButton: {
              onClick: () => onSuccess(TroubleshootingStep.RestartSuccess),
              children: formatMessage({ id: 'yes' }),
            },
            secondaryButton: {
              onClick: onFail,
              children: formatMessage({ id: 'no' }),
              type: 'secondary',
            },
          },
        };
      }
      case TroubleshootingStep.Fail: {
        return {
          message: (
            <>
              {formatMessage(
                {
                  id: 'troubleshooting.helpCenterMessage',
                },
                {
                  link: (
                    <AppLink
                      target='_blank'
                      rel='noreferrer'
                      href={formatMessage({
                        id: 'troubleshooting.helpCenterLink',
                      })}
                    >
                      <LinkM>
                        {formatMessage({ id: 'troubleshooting.helpCenter' })}
                      </LinkM>
                    </AppLink>
                  ),
                },
              )}
            </>
          ),
          actions: {
            submitButton: {
              onClick: onClose,
              children: formatMessage({ id: 'done' }),
            },
          },
        };
      }
      default: {
        return undefined;
      }
    }
  };

  const stepDetails = getStepDetails();

  if (!stepDetails) {
    return null;
  }

  const { message, actions } = stepDetails;

  return (
    <Modal isOpen={isOpen} aria-labelledby={titleId}>
      <ModalContent>
        <ModalHeader
          titleId={titleId}
          onClose={onClose}
          title={formatMessage({ id: 'troubleshooting' })}
        />
        <ModalBody withFooter>
          <BodyM color={theme.text.generic.secondary}>{message}</BodyM>
        </ModalBody>
        <ModalFooter {...actions} />
      </ModalContent>
    </Modal>
  );
};
